import React, { useState, useEffect } from "react"
import { useFormContext } from "react-hook-form"
import styled, { css } from "styled-components"
import PropTypes from "prop-types"
import { Label, Error } from "./input-v2.styled"

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  font-family: "system-ui";
`

const DropdownHeader = styled.div`
  padding: 12px 16px;
  border: 1px solid ${props => (props.$hasError ? "red" : "#D0D5DD")};
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  color: ${props => props.theme.blackShade || "#2d3748"};
  ${props =>
    props.$isPlaceholder &&
    css`
      color: ${props => props.theme.gray500};
    `}
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${props => (props.$hasError ? "red" : "#D0D5DD")};
  }

  .chevron {
    position: absolute;
    display: block;
    right: 10px;
    bottom: 10px;
    ${props =>
      props.isOpen &&
      css`
        transform: rotate(180deg);
      `}
  }
`

const DropdownList = styled.ul`
  position: absolute;
  width: 100%;
  background-color: white;
  border: 1px solid "#D0D5DD";
  border-radius: 8px;
  margin-top: 8px;
  padding: 8px 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  list-style: none;
  z-index: 100;
`

const DropdownItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  color: ${props => props.theme.blackShade || "#2d3748"};
  background-color: ${props =>
    props.selected ? props.theme.secondary50 : "transparent"};

  &:hover {
    background-color: ${props => props.theme.secondary50};
  }

  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DropdownContainer = styled.div``

const InputDropdown = ({
  name,
  label,
  options = ["1 traveller", "2 travellers", "3 travellers", "4 travellers"],
  defaultValue,
  required,
  validate,
  placeholder = "Select an option",
}) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext()

  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(defaultValue || "")

  useEffect(() => {
    setValue(name, selectedOption)
  }, [selectedOption, setValue, name])

  const toggleDropdown = () => setIsOpen(!isOpen)

  const handleSelect = option => {
    setSelectedOption(option)
    setIsOpen(false)
  }

  return (
    <DropdownContainer className="input-dropdown">
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}

      <DropdownWrapper>
        <DropdownHeader
          onClick={toggleDropdown}
          $hasError={!!errors[name]}
          id={name}
          isOpen={isOpen}
          $isPlaceholder={!selectedOption}
        >
          {selectedOption || placeholder}
          <Chevron />
        </DropdownHeader>

        {isOpen && (
          <DropdownList>
            {options.map(option => (
              <DropdownItem
                key={option}
                onClick={() => handleSelect(option)}
                selected={option === selectedOption}
              >
                {option}
                {option === selectedOption && <Checkmark />}
              </DropdownItem>
            ))}
          </DropdownList>
        )}
      </DropdownWrapper>

      <input
        type="hidden"
        {...register(name, { required, validate })}
        value={selectedOption}
      />

      {errors[name] && (
        <Error dangerouslySetInnerHTML={{ __html: errors[name].message }} />
      )}
    </DropdownContainer>
  )
}

InputDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  validate: PropTypes.func,
  placeholder: PropTypes.string,
}

export default InputDropdown

const Chevron = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="chevron"
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="#667085"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const Checkmark = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M16.6667 5L7.50004 14.1667L3.33337 10"
        stroke="#26383B"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
